import DOMPurify from "dompurify";
import { isString } from "lodash";

/**
 * 解码 HTML 实体（如 &lt; -> <, &gt; -> >, &amp; -> &）
 * 用于将转义的 HTML 标签还原
 */
export function decodeHTMLEntities(str: string): string {
  if (!isString(str)) return str;
  if (typeof window === "undefined") {
    // 服务端环境，使用简单的替换
    return str
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&");
  }
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
}

export function decodeHTML(str: string) {
  if (isString(str)) {
    if (typeof window === "undefined") {
      return str;
    }
    const div = document.createElement("div");
    div.innerHTML = str;
    return div.textContent || div.innerText;
  }
  return str;
}

export function sanitizeHTML(str: string) {
  if (isString(str)) {
    // 服务端环境下，DOMPurify 不可用，直接返回原字符串
    if (typeof window === "undefined") {
      return str;
    }
    return DOMPurify.sanitize(str, {
      SAFE_FOR_TEMPLATES: true,
      ADD_ATTR: ["target"],
    });
  }
  return str;
}

/**
 * 判断消息是否是系统代发的（包含结构化HTML内容）
 * 系统代发的消息通常包含：
 * - 完整的HTML标签结构（如 <a href="...">, <div>, <span> 等）
 * - 链接标签
 * - 规范的HTML格式
 *
 * 用户输入的HTML标签（如 <script>, <img> 等）不会被识别为系统消息
 * @param str 消息文本（可能包含转义的HTML实体）
 * @returns 是否是系统代发的消息
 */
export function isSystemGeneratedMessage(str: string): boolean {
  if (!isString(str)) return false;

  // 先解码 HTML 实体（系统消息中的 HTML 标签可能被转义为 &lt; &gt;）
  const decodedStr = decodeHTMLEntities(str);

  // 检测是否包含HTML标签
  const htmlTagRegex = /<\/?[a-z][\s\S]*>/i;
  if (!htmlTagRegex.test(decodedStr)) {
    return false; // 不包含HTML标签，肯定是用户输入
  }

  // 4. 不包含危险的标签（如果包含这些，更可能是用户恶意输入）
  const hasDangerousTags =
    /<(script|iframe|object|embed|form|input|textarea)/i.test(decodedStr);

  // 如果包含危险标签，认为是用户输入（需要纯文本渲染）
  if (hasDangerousTags) {
    return false;
  }

  // 系统消息的特征：
  // 1. 包含链接标签（系统消息通常有可点击的链接）
  // 放宽匹配：<a 后面可能有空格，href 可能在任意位置
  const hasLinkTag = /<a\b[^>]*\bhref\s*=/i.test(decodedStr);

  // 2. 包含常见的结构化标签（div, span, button等）
  const hasStructuredTag = /<(div|span|button|p)\b[\s>]/i.test(decodedStr);

  // 3. HTML标签结构相对完整（有开始和结束标签，或自闭合标签）
  // 放宽匹配：允许标签之间有文本内容
  const hasCompleteTags =
    /<[a-z]+[^>]*>[\s\S]*<\/[a-z]+>/i.test(decodedStr) ||
    /<[a-z]+[^>]*\/\s*>/i.test(decodedStr);

  // 系统消息的判断条件：
  // - 如果包含链接标签，且标签结构完整，则认为是系统消息
  // - 或者包含结构化标签且标签结构完整
  if (hasLinkTag) {
    // 有链接标签时，检查是否有完整的 <a>...</a> 结构
    return /<a\b[^>]*>[\s\S]*<\/a>/i.test(decodedStr);
  }

  // 没有链接但有结构化标签，且标签完整
  return hasStructuredTag && hasCompleteTags;
}
